import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { SplitText as GSAPSplitText } from 'gsap/SplitText';

gsap.registerPlugin(ScrollTrigger, GSAPSplitText);

const SplitText = ({
  text,
  className = '',
  delay = 100,
  duration = 0.6,
  ease = 'power3.out',
  splitType = 'chars', // 'chars' | 'words' | 'lines' or combinations like 'words,lines'
  from = { opacity: 0, y: 40 },
  to = { opacity: 1, y: 0 },
  initialDelay = 0, // ms before starting the tween
  threshold = 0.1,
  rootMargin = '-100px',
  textAlign = 'left',
  tag = 'p',
  onLetterAnimationComplete,
}) => {
  const ref = useRef(null);
  const splitInstanceRef = useRef(null);
  const [fontsLoaded, setFontsLoaded] = useState(false);

  useEffect(() => {
    if (document.fonts?.status === 'loaded') {
      setFontsLoaded(true);
    } else if (document.fonts?.ready) {
      document.fonts.ready.then(() => setFontsLoaded(true));
    } else {
      // Fallback: proceed after a tick if Font Loading API isn't available
      const t = setTimeout(() => setFontsLoaded(true), 50);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el || !text || !fontsLoaded) return;

    // Clean any previous split
    if (splitInstanceRef.current) {
      try { splitInstanceRef.current.revert(); } catch (_) {}
      splitInstanceRef.current = null;
    }

    const startPct = (1 - threshold) * 100;
    const marginMatch = /^(-?\d+(?:\.\d+)?)(px|em|rem|%)?$/.exec(rootMargin);
    const marginValue = marginMatch ? parseFloat(marginMatch[1]) : 0;
    const marginUnit = marginMatch ? marginMatch[2] || 'px' : 'px';
    const sign = marginValue === 0 ? '' : marginValue < 0 ? `-=${Math.abs(marginValue)}${marginUnit}` : `+=${marginValue}${marginUnit}`;
    const start = `top ${startPct}%${sign}`;

    let targets;
    const assignTargets = (self) => {
      if (!targets && splitType.includes('chars') && self.chars?.length) targets = self.chars;
      if (!targets && splitType.includes('words') && self.words?.length) targets = self.words;
      if (!targets && splitType.includes('lines') && self.lines?.length) targets = self.lines;
      if (!targets) targets = self.chars || self.words || self.lines;
    };

    const instance = new GSAPSplitText(el, {
      type: splitType,
      smartWrap: true,
      autoSplit: splitType === 'lines',
      linesClass: 'split-line',
      wordsClass: 'split-word',
      charsClass: 'split-char',
      reduceWhiteSpace: false,
      onSplit: (self) => {
        assignTargets(self);
        gsap.fromTo(
          targets,
          { ...from },
          {
            ...to,
            duration,
            ease,
            delay: initialDelay / 1000,
            stagger: delay / 1000,
            scrollTrigger: {
              trigger: el,
              start,
              once: true,
              fastScrollEnd: true,
              anticipatePin: 0.4,
            },
            onComplete: () => {
              onLetterAnimationComplete?.();
            },
            willChange: 'transform, opacity',
            force3D: true,
            clearProps: 'transform,opacity',
          }
        );
      },
    });

    splitInstanceRef.current = instance;

    return () => {
      // Kill only triggers related to this element
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger === el) st.kill(true);
      });
      try { instance.revert(); } catch (_) {}
      splitInstanceRef.current = null;
    };
  }, [text, delay, duration, ease, splitType, from, to, threshold, rootMargin, fontsLoaded, onLetterAnimationComplete, initialDelay]);

  const style = { textAlign, wordWrap: 'break-word', willChange: 'transform, opacity' };
  const classes = `split-parent inline-block whitespace-normal ${className}`;

  switch (tag) {
    case 'h1':
      return <h1 ref={ref} style={style} className={classes}>{text}</h1>;
    case 'h2':
      return <h2 ref={ref} style={style} className={classes}>{text}</h2>;
    case 'h3':
      return <h3 ref={ref} style={style} className={classes}>{text}</h3>;
    case 'h4':
      return <h4 ref={ref} style={style} className={classes}>{text}</h4>;
    case 'h5':
      return <h5 ref={ref} style={style} className={classes}>{text}</h5>;
    case 'h6':
      return <h6 ref={ref} style={style} className={classes}>{text}</h6>;
    default:
      return <p ref={ref} style={style} className={classes}>{text}</p>;
  }
};

export default SplitText;


